// Globals
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Button, Flex, Table } from 'antd';

// Locals
import styles from './Account.module.css';
import useUser from '../../hooks/useUser';
import { setPaymentState, setUpgradeModalOpen } from '../../actions';
import usePaddleAccountDetails from '../../hooks/useQuery/usePaddleAccountDetails';
import usePaddleTransactions from '../../hooks/useQuery/usePaddleTransactions';
import { PaymentState } from '../../types';
import { ColumnsType } from 'antd/es/table';
import { LoadingOutlined } from '@ant-design/icons';
import { colors } from '../../theme';

const Loader = (
  <LoadingOutlined style={{ color: colors.black1, fontSize: '32px' }} spin />
);

const AccountSubscription: React.FC = () => {
  const Paddle = window['Paddle'];

  const dispatch = useDispatch();
  const { user } = useUser();

  const [currentPlanID, setCurrentPlanID] = useState<number | null | undefined>(
    undefined
  );

  const {
    data: accountDetailsData,
    refetch: refetchAccountDetails,
    loading: accountDetailsLoading,
    error: accountDetailsError,
  } = usePaddleAccountDetails(currentPlanID);

  const {
    data: transactionsData,
    refetch: refetchTransactions,
    loading: transactionsLoading,
    error: transactionsError,
  } = usePaddleTransactions(currentPlanID);

  useEffect(() => {
    if (user && user.plan && user.plan.subscriptionIDs) {
      setCurrentPlanID(
        user!.plan!.subscriptionIDs![user!.plan!.subscriptionIDs!.length - 1]
      );
    }
    if (
      user!.plan &&
      user!.plan!.subscriptionIDs &&
      user!.plan!.subscriptionIDs.length > 0
    ) {
      refetchAccountDetails();
      refetchTransactions();
    }
  }, [
    user,
    refetchAccountDetails,
    refetchTransactions,
    // needToRefetchSubData,
    dispatch,
  ]);

  const handlePaddleRequest = useCallback(
    (requestType: string) => e => {
      e.preventDefault();
      // subscribe();
      Paddle.Checkout.open({
        override: accountDetailsData![requestType],
        successCallback: () => {
          if (requestType !== 'cancelUrl') {
            dispatch(setPaymentState(PaymentState.processing));
          }

          console.log(`Paddle ${requestType} success`);
        },
        closeCallback: () => {
          console.log(`Paddle ${requestType} failure`);
        },
      });
    },
    [Paddle.Checkout, accountDetailsData, dispatch]
  );

  const handleChangePlans = () => e => {
    e.preventDefault();
    dispatch(setUpgradeModalOpen(true));
  };

  const handleOrderHistory = () => e => {
    e.preventDefault();
    Paddle.User.History(accountDetailsData!.email, null, response => {
      if (response.success) {
        alert('Please check your email for your order history.');
      }
    });
  };

  const capitalize = (string: string) =>
    string.charAt(0).toUpperCase() + string.slice(1);

  function formatDate(date?: string) {
    if (!date) {
      return '';
    }
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const dateParts = date.split(' ')[0].split('-');
    return `${months[Number(dateParts[1]) - 1]} ${dateParts[2]}, ${
      dateParts[0]
    }`;
  }

  interface DataType {
    key: string;
    name: string;
    value?: string | number | null;
  }

  const accountDetailColumnFormat: ColumnsType<DataType> = useMemo(
    () => [
      {
        title: 'name',
        key: 'name',
        dataIndex: 'name',
        width: '50%',
        render: x => <b>{x}</b>,
      },
      {
        title: 'value',
        dataIndex: 'value',
        key: 'value',
      },
    ],
    []
  );

  const subscriptionData: DataType[] = useMemo(() => {
    let data: DataType[] = [];

    if (user) {
      data.push({
        key: 'Email',
        name: 'Email',
        value: user && user.email,
      });
    }

    if (accountDetailsData) {
      data.push({
        key: 'Subscriber ID',
        name: 'Subscriber ID',
        value: accountDetailsData && accountDetailsData!.userId,
      });

      data.push({
        key: 'Plan',
        name: 'Plan',
        value: accountDetailsData!.planName,
      });
    }

    if (accountDetailsData) {
      data.push({
        key: 'Subscribed',
        name: 'Subscribed',
        value:
          accountDetailsData &&
          accountDetailsData!.subscriptionState === 'deleted'
            ? '(canceled)'
            : accountDetailsData!.signupDate
            ? formatDate(accountDetailsData!.signupDate)
            : 'Never',
      });
    }

    return data;
  }, [user, accountDetailsData]);

  const accountDetails = (
    <Table
      style={{ width: '100%' }}
      size={'small'}
      pagination={false}
      bordered={false}
      loading={accountDetailsLoading ? { indicator: Loader } : false}
      dataSource={subscriptionData}
      columns={accountDetailColumnFormat}
      showHeader={false}
      title={() => <b style={{ fontSize: '22px' }}>Subscription</b>}
      footer={() => (
        <Flex gap={'small'} justify={'flex-end'}>
          {accountDetailsData &&
            accountDetailsData!.subscriptionState === 'active' && (
              <Button
                danger={true}
                type={'text'}
                onClick={handlePaddleRequest('cancelUrl')}
              >
                Cancel Subscription
              </Button>
            )}

          <Button type={'default'} onClick={handleChangePlans()}>
            Change Plan
          </Button>
        </Flex>
      )}
    ></Table>
  );

  const paymentData: DataType[] = useMemo(() => {
    if (accountDetailsData?.nextPaymentAmount) {
      return [
        {
          key: 'Method',
          name: 'Method',
          value: accountDetailsData!.cardType
            ? capitalize(accountDetailsData!.cardType!)
            : 'Card ending in ' + accountDetailsData?.lastFourDigits,
        },
        {
          key: 'Amount',
          name: 'Last Payment Amount',
          value: 'US $' + accountDetailsData?.lastPaymentAmount,
        },
        {
          key: 'Next Payment',
          name: 'Next Payment',
          value: accountDetailsData!.nextPaymentDate
            ? formatDate(accountDetailsData!.nextPaymentDate)
            : '',
        },
      ];
    } else {
      return [];
    }
  }, [accountDetailsData]);

  const paymentDetails = useMemo(() => {
    return (
      <Table
        style={{ width: '100%' }}
        size={'small'}
        pagination={false}
        bordered={false}
        loading={accountDetailsLoading ? { indicator: Loader } : false}
        dataSource={paymentData}
        columns={accountDetailColumnFormat}
        showHeader={false}
        title={() => <b style={{ fontSize: '22px' }}>Payment</b>}
        footer={() => (
          <Flex vertical={false} justify={'flex-end'}>
            <Button type={'default'} onClick={handlePaddleRequest('updateUrl')}>
              Update Payment Method
            </Button>
          </Flex>
        )}
      ></Table>
    );
  }, [
    accountDetailColumnFormat,
    accountDetailsLoading,
    handlePaddleRequest,
    paymentData,
  ]);

  const transactionsTableCols = [
    {
      key: 'Date',
      title: 'Date',
      dataIndex: 'date',
    },
    {
      key: 'Company',
      title: 'Company',
      dataIndex: 'company',
    },
    {
      key: 'Amount',
      title: 'Amount',
      dataIndex: 'amount',
    },
    {
      key: 'Receipt',
      title: 'Receipt',
      dataIndex: 'receipt',
    },
  ];

  const transactionsTableData = useMemo(() => {
    if (!transactionsData?.transactions) {
      return [];
    }

    return transactionsData!.transactions!.map((tx, i) => {
      return {
        key: 'tx-item-' + i,
        date: formatDate(tx!.createdAt!),
        company: 'Giide Inc',
        amount: 'USD $' + tx!.amount,
        receipt: (
          <a href={tx!.receiptUrl!} target="_blank" rel="noreferrer">
            View Receipt
          </a>
        ),
      };
    });
  }, [transactionsData]);

  if (accountDetailsError || transactionsError) {
    return (
      <div className={styles.subscriptionError}>
        Oops! Looks like something went wrong. Please try again later.
      </div>
    );
  }

  return (
    <div>
      <div className={styles.subscriptionDashboard}>
        <Flex
          justify={'flex-start'}
          align={'flex-start'}
          gap={'large'}
          vertical={true}
          style={{ width: '50%' }}
        >
          {accountDetails}
          {paymentDetails}
        </Flex>

        <Table
          dataSource={transactionsTableData}
          columns={transactionsTableCols}
          size={'small'}
          loading={transactionsLoading ? { indicator: Loader } : false}
          title={() => (
            <Flex justify={'space-between'}>
              <b style={{ fontSize: '22px' }}>Transaction History</b>
              <Button type={'default'} onClick={handleOrderHistory()}>
                Email full history
              </Button>
            </Flex>
          )}
        ></Table>
      </div>
    </div>
  );
};

export default AccountSubscription;
